// One roster chip = one session (a block in Template mode, an expanded
// occurrence in Week mode). Shows the block label + span + the "other"
// dimension (location in doctor rows, doctor in location rows), tinted by the
// block color or the other entity's stable hue. Week mode adds override
// states: cancelled ghost, moved-out (dashed, → target), moved-in (← origin),
// relocated (swap icon + emphasized location).

import { ArrowLeftRight, ArrowRight, ArrowLeft, Ban, CircleDot } from "lucide-react";
import { DateTime } from "luxon";
import { cn } from "@/lib/utils";
import { chipTone, occurrenceState } from "./sessions-utils";

function shortDate(iso) {
  const dt = DateTime.fromISO(String(iso || ""));
  return dt.isValid ? dt.toFormat("d LLL") : iso;
}

export function SessionChip({
  label,
  timeRange,
  otherName,
  toneId,
  colorHex,
  badge,
  occurrence,
  onClick,
  disabled = false,
  title,
}) {
  const tone = chipTone(toneId, colorHex);
  const state = occurrence ? occurrenceState(occurrence) : "normal";
  const override = occurrence?.override || null;

  const stateBits = (() => {
    switch (state) {
      case "cancelled":
        return {
          icon: <Ban className="size-3 shrink-0" />,
          suffix: null,
          chipClass: "opacity-45",
          textClass: "line-through",
          tooltip: `Cancelled this day${override?.note ? `: ${override.note}` : ""}`,
        };
      case "moved-out":
        return {
          icon: <ArrowRight className="size-3 shrink-0" />,
          suffix: shortDate(override?.toDate),
          chipClass: "border-dashed opacity-55",
          textClass: "line-through",
          tooltip: `Moved to ${shortDate(override?.toDate)}${override?.note ? `: ${override.note}` : ""}`,
        };
      case "moved-in":
        return {
          icon: <ArrowLeft className="size-3 shrink-0" />,
          suffix: shortDate(override?.sourceDate),
          chipClass: "",
          textClass: "",
          tooltip: `Moved here from ${shortDate(override?.sourceDate)}${override?.note ? `: ${override.note}` : ""}`,
        };
      case "relocated":
        return {
          icon: <ArrowLeftRight className="size-3 shrink-0" />,
          suffix: null,
          chipClass: "",
          textClass: "",
          tooltip: `Relocated this day${override?.note ? `: ${override.note}` : ""}`,
        };
      default:
        return { icon: null, suffix: null, chipClass: "", textClass: "", tooltip: "" };
    }
  })();

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !onClick}
      title={title || stateBits.tooltip || undefined}
      className={cn(
        "group/chip flex w-full items-center gap-1.5 rounded-md border px-1.5 py-1 text-left text-[11px] leading-tight transition-colors",
        onClick && !disabled ? "cursor-pointer hover:brightness-110" : "cursor-default",
        stateBits.chipClass,
      )}
      style={{
        background: tone.soft,
        borderColor: `color-mix(in oklab, ${tone.solid} 45%, transparent)`,
      }}
    >
      <span
        aria-hidden
        className="h-6 w-1 shrink-0 rounded-full"
        style={{ background: tone.solid }}
      />
      <span className="min-w-0 flex-1">
        <span className={cn("flex items-center gap-1 font-semibold", stateBits.textClass)}>
          <span className="truncate">
            {label ? `${label} ` : ""}
            <span className="font-mono text-[10px] font-medium tabular-nums text-foreground/80">
              {timeRange}
            </span>
          </span>
          {stateBits.icon}
          {stateBits.suffix ? (
            <span className="shrink-0 font-mono text-[10px] tabular-nums">{stateBits.suffix}</span>
          ) : null}
        </span>
        <span className={cn("flex items-center gap-1 text-[10px] text-muted-foreground", stateBits.textClass)}>
          <span className={cn("truncate", state === "relocated" && "font-semibold text-foreground/90")}>
            {otherName}
          </span>
          {badge ? (
            <span className="shrink-0 rounded-sm bg-foreground/10 px-1 font-mono text-[9px] tabular-nums">
              {badge}
            </span>
          ) : null}
        </span>
      </span>
      {override ? (
        <CircleDot className="size-2.5 shrink-0 text-foreground/60" aria-label="Overridden" />
      ) : null}
    </button>
  );
}
